import { useCallback, useEffect, useMemo } from "react";
import {
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
} from "@chakra-ui/react";

import { Button } from "newui/components/Button";
import {
	SpreadsheetGrid,
	useColumnManager,
} from "../../SpreadsheetEditor";
import type {
	ColumnConfig,
	QuoteColumnType,
} from "../../SpreadsheetEditor";
import type {
	LocalColumn,
	ScheduleColumn,
	ScheduleColumnType,
} from "../schedule-of-rates.types";
import { SchedulePreviewTable } from "./ScheduleOfRatesPreviewTable";

export interface EditScheduleOfRatesModalProps {
	initialColumns: ScheduleColumn[];
	isOpen: boolean;
	isSaving?: boolean;
	title?: string;
	onClose: () => void;
	onSave: (columns: ScheduleColumn[]) => void;
}

// ---------------------------------------------------------------------------
// Schedule <-> SpreadsheetEditor column-type mapping
//
// SpreadsheetEditor (useColumnManager + SpreadsheetGrid) is generic over
// QuoteColumnType. Schedule-of-rates has its own narrower set, so we map at
// the boundaries.
// ---------------------------------------------------------------------------

const SCHEDULE_TO_QUOTE: Record<ScheduleColumnType, QuoteColumnType> = {
	text: "response_text",
	number: "response_number",
	currency: "response_value",
	unit_of_measurement: "unit_of_measurement",
};

const QUOTE_TO_SCHEDULE: Partial<Record<QuoteColumnType, ScheduleColumnType>> = {
	response_text: "text",
	response_number: "number",
	response_value: "currency",
	unit_of_measurement: "unit_of_measurement",
};

const ALLOWED_QUOTE_TYPES = new Set<QuoteColumnType>([
	"response_text",
	"response_number",
	"response_value",
	"unit_of_measurement",
]);

const EditScheduleOfRatesModal: React.FC<EditScheduleOfRatesModalProps> = ({
	initialColumns,
	isOpen,
	isSaving = false,
	onClose,
	onSave,
	title = "Edit Schedule Columns",
}) => {
	const {
		addColumn,
		columns: editorColumns,
		getDisabledReason: hookGetDisabledReason,
		insertColumn,
		isTypeDisabled: hookIsTypeDisabled,
		moveColumn,
		removeColumn,
		reorderColumns,
		resetForEdit,
		updateColumnLabel,
		updateColumnType,
	} = useColumnManager();

	// Reset draft state when modal opens with new data — map schedule columns
	// into the SpreadsheetEditor's ColumnConfig shape.
	useEffect(() => {
		if (isOpen) {
			const initial: ColumnConfig[] = initialColumns.map((c, idx) => ({
				id: c.id,
				columnType: SCHEDULE_TO_QUOTE[c.columnType],
				label: c.label,
				localId: `sched-col-${c.id}-${idx}`,
				sortOrder: c.sortOrder,
			}));
			resetForEdit(initial);
		}
	}, [isOpen, initialColumns, resetForEdit]);

	// Constrain the column-type chooser to the four types schedule supports.
	const isTypeDisabled = useCallback(
		(type: QuoteColumnType, currentColType?: QuoteColumnType) => {
			if (!ALLOWED_QUOTE_TYPES.has(type)) return true;
			return hookIsTypeDisabled(type, currentColType);
		},
		[hookIsTypeDisabled]
	);

	const getDisabledReason = useCallback(
		(type: QuoteColumnType) => {
			if (!ALLOWED_QUOTE_TYPES.has(type)) {
				return "Not supported in Schedule of Rates";
			}
			return hookGetDisabledReason(type);
		},
		[hookGetDisabledReason]
	);

	// Preview-table data: map back from QuoteColumnType to LocalColumn,
	// generating sequential negative numeric ids for new (id-less) entries.
	const previewColumns: LocalColumn[] = useMemo(() => {
		let nextNegId = -1;
		return editorColumns.map((c) => ({
			id: c.id ?? nextNegId--,
			columnType: QUOTE_TO_SCHEDULE[c.columnType] ?? "text",
			label: c.label,
			localId: c.localId,
			sortOrder: c.sortOrder,
		}));
	}, [editorColumns]);

	const handleSave = useCallback(() => {
		if (editorColumns.length === 0) {
			return;
		}
		// Map the hook's ColumnConfig[] back to ScheduleColumn[]. Sequential
		// negative ids are generated for columns added in this session so the
		// outer remap (label+type based) can still pair old and new rows.
		let nextNegId = -1;
		const next: ScheduleColumn[] = editorColumns.map((c) => ({
			id: c.id ?? nextNegId--,
			columnType: QUOTE_TO_SCHEDULE[c.columnType] ?? "text",
			label: c.label,
			sortOrder: c.sortOrder,
		}));
		onSave(next);
	}, [editorColumns, onSave]);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size="full"
			scrollBehavior="inside"
		>
			<ModalOverlay />
			<ModalContent data-testid="edit-schedule-modal">
				<ModalHeader>{title}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<div className="flex gap-6">
						{/* Left side: Column configuration via SpreadsheetEditor */}
						<div className="w-1/2">
							<p className="mb-2 font-roboto text-sm font-semibold text-gray-900">
								Columns
							</p>
							<SpreadsheetGrid
								columns={editorColumns}
								getDisabledReason={getDisabledReason}
								isTypeDisabled={isTypeDisabled}
								onAddColumn={addColumn}
								onInsertColumn={insertColumn}
								onMoveColumn={moveColumn}
								onRemoveColumn={removeColumn}
								onReorderColumns={reorderColumns}
								onUpdateLabel={updateColumnLabel}
								onUpdateType={updateColumnType}
							/>
						</div>

						{/* Right side: Preview */}
						<div className="w-1/2">
							<p className="mb-2 font-roboto text-sm font-semibold text-gray-900">
								Preview
							</p>
							<SchedulePreviewTable columns={previewColumns} />
						</div>
					</div>
				</ModalBody>
				<ModalFooter>
					<Button
						btntype="secondary"
						className="mr-3"
						data-testid="cancel-edit-schedule"
						onClick={onClose}
					>
						Cancel
					</Button>
					<Button
						btntype="primary"
						data-testid="save-edit-schedule"
						disabled={isSaving || editorColumns.length === 0}
						onClick={handleSave}
					>
						{isSaving ? "Saving..." : "Save"}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export { EditScheduleOfRatesModal as EditScheduleModal };
